/** Sentences the CEO board prints about its own figures, kept beside the page
 *  so each one can be pinned by a test without rendering the board. */

import type { CityConcentrationData } from '@/components/dashboards/CityConcentrationMap'
import { originText } from '@/components/dashboards/coordinateOrigin'

/** The concentration footer. With no policy limit configured there is nothing
 *  to have headroom against — "Every configured limit has headroom" over zero
 *  configured limits read as an all-clear nobody measured (stress test EXEC-14). */
export function concentrationFooter(
  rows: { limitPct: number | null }[],
  breached: string[],
): string | undefined {
  if (rows.length === 0) return undefined
  if (!rows.some((row) => row.limitPct != null)) {
    return 'No concentration limits are configured — shares are the observed book mix, not tested against a policy.'
  }
  if (breached.length > 0) return `At or near limit: ${breached.join(', ')}`
  return 'Every configured limit has headroom.'
}

/** The Production & earnings outlook kicker. When production is the live
 *  book's funded volume, the months counted are months with a funding — not
 *  finance closes — and "1 closed" printed above "No finance close is on
 *  record" contradicted the footer (stress test EXEC-01, verifier 26 Sep). */
export function outlookKicker(outlook: {
  closedMonths: number
  fundedMonths?: number | null
  forecastMonths: number
}): string {
  const funded = outlook.fundedMonths ?? 0
  const actual =
    outlook.closedMonths === 0 && funded > 0
      ? `${funded} month${funded === 1 ? '' : 's'} funded · no close`
      : `${outlook.closedMonths} closed`
  return `${actual} · ${outlook.forecastMonths} forecast`
}

/** What the map's points are, where each one came from, and how precisely each
 *  is placed.
 *
 *  The panel itself geocodes nothing — it reads the coordinate on each property
 *  row. The sourcing sweep does geocode addresses onto those rows, and a
 *  geocoded point is plotted without anyone reviewing it, so the footnote names
 *  those points and the service instead of letting "stored" suggest a person
 *  vetted them (stress test EXEC-23). A city-level point is the municipality's
 *  own centre; saying so is the difference between a concentration map and a
 *  claim about where a building stands.
 */
export function mapFootnote(data: CityConcentrationData): string {
  const parts = [data.coordinateSource, data.coordinateAggregation]
  const origin = originText(data, 'mapped deals')
  if (origin) parts.push(origin)
  const asset = data.assetLevelMappedDeals
  const city = data.cityLevelMappedDeals
  if (typeof asset === 'number' && typeof city === 'number' && asset + city > 0) {
    parts.push(
      city === 0
        ? `All ${asset.toLocaleString()} mapped deals resolved to a street address`
        : asset === 0
          ? `All ${city.toLocaleString()} mapped deals placed at city center only`
          : `${asset.toLocaleString()} placed by street address, ${city.toLocaleString()} at city center only`,
    )
  }
  const unrecorded = data.coordinatePrecision?.unrecorded ?? 0
  if (unrecorded > 0) {
    parts.push(`${unrecorded.toLocaleString()} stored without recorded precision`)
  }
  return `${parts.join('. ')}.`
}
